<template>
  <div
    class="container checkoutPage"
    :class="{ 'scrollable-container': pathMixin_isMeeting }"
  >
    <div v-if="!pathMixin_isMeeting" class="has-text-centered gant-text mb-5">
      <h1 class="title gant-text">Almost there!</h1>
      <h2 class="subtitle gant-text">
        Review your itinerary and submit your booking.
      </h2>
    </div>

    <div class="columns">
      <div class="column" v-if="!isDesktop">
        <div v-if="isNDC" class="mb-3 mx-2">
          <b-notification
            :type="countdown > 0 ? 'is-warning' : 'is-danger'"
            :closable="false"
            position="is-top-right"
            has-icon
            icon="clock"
            icon-size="is-small"
          >
            <div v-if="countdown > 0" class="is-size-5 mt-2">
              Hurry! This fare expires in {{ minutes }} minutes
              {{ seconds }} seconds.
            </div>
            <div v-else class="is-size-5 mt-2">
              Sorry, this fare has expired.
            </div>
          </b-notification>

          <b-message
            title="Airline Direct (NDC) Fare"
            type="is-success"
            aria-close-label="Close message"
          >
            <ul>
              <li>
                This booking contains a direct airline fare (NDC) and may
                contain special pricing.
              </li>
            </ul>
          </b-message>
        </div>

        <div class="boxWithBorder mb-3">
          <div class="" v-if="!noTravelBooked">
            <div class="columns is-mobile is-vcentered">
              <div class="column is-7">
                <h3
                  class="is-size-6 is-uppercase has-text-grey-darker has-text-weight-bold"
                >
                  Total Trip Estimate
                </h3>
              </div>
              <div class="column is-4" style="text-align: right;">
                <h3 class="is-size-5 has-text-grey">
                  {{
                    pricingInfo.total
                      | formatPriceFromAmount(0, flightCurrency)
                  }}
                </h3>
              </div>
              <div
                @click="priceExpanded = !priceExpanded"
                style="padding-left: 0"
                class="column is-1"
              >
                <b-icon
                  style="padding-top: 10px; cursor: pointer;"
                  icon="chevron-down"
                />
              </div>
            </div>
            <div v-if="priceExpanded">
              <hr />
              <div v-for="(flight, index) in pricingInfo.flight" :key="index">
                <span
                  class="is-block is-size-6 has-text-weight-bold my-2 segment-title-line"
                >
                  {{ flight.message }}
                </span>
                <span class="is-size-6">
                  Fare
                </span>
                <span class="is-block is-size-6 is-pulled-right">
                  {{
                    flight.fare | formatPriceFromAmount(0, flightCurrency)
                  }} </span
                ><br />
                <div v-if="flight.seatFees > 0">
                  <span class="is-size-6 has-text-grey-light">
                    Seat Fees
                  </span>
                  <span class="is-size-6 is-pulled-right has-text-grey-light">
                    {{
                      flight.seatFees
                        | formatPriceFromAmount(0, flightCurrency)
                    }} </span
                  ><br />
                </div>
                <span class="is-size-6 has-text-grey-light">
                  Taxes
                </span>
                <span class="is-size-6 is-pulled-right has-text-grey-light">
                  {{
                    flight.tax | formatPriceFromAmount(0, flightCurrency)
                  }}
                </span>

                <div class="estimated-total-line">
                  <span class="is-size-6">
                    Estimated Total
                  </span>
                  <span class="is-size-6 is-pulled-right mb-2">
                    <!-- {{ pricingInfo.flight.total + seatPricing.total| formatPrice}} -->
                    {{
                      flight.total
                        | formatPriceFromAmount(0, flightCurrency)
                    }}
                  </span>
                </div>
              </div>
              <div v-if="pricingInfo.car">
                <br />
                <span
                  class="is-block is-size-6 has-text-weight-bold mb-2 segment-title-line"
                >
                  {{ pricingInfo.car.message }}
                </span>
                <span class="is-size-6">
                  Rental charge ({{ pricingInfo.car.days }} days @
                  {{
                    pricingInfo.car.rate
                      | formatPriceFromAmount(0, userDefaultCurrency)
                  }}/day)
                </span>
                <span class="is-block is-size-6 is-pulled-right">
                  {{
                    pricingInfo.car.fare
                      | formatPriceFromAmount(0, userDefaultCurrency)
                  }} </span
                ><br />
                <div v-if="pricingInfo.car.tax">
                  <span class="is-size-6 has-text-grey-light">
                    Taxes
                  </span>
                  <span class="is-size-6 is-pulled-right has-text-grey-light">
                    {{
                      pricingInfo.car.tax
                        | formatPriceFromAmount(0, userDefaultCurrency)
                    }} </span
                  ><br />
                </div>

                <div class="estimated-total-line">
                  <span class="is-size-6">
                    Estimated Total
                  </span>
                  <span class="is-size-6 is-pulled-right mb-2">
                    <b-tooltip
                      v-if="pricingInfo.car.isConversion"
                      class="is-size-5"
                      type="is-dark"
                      position="is-bottom"
                      multilined
                    >
                      <template v-slot:content>
                        {{ pricingInfo.car.currencyConvertedMessage }}
                      </template>
                      <b-icon
                        type="is-success"
                        icon="approximately-equal"
                        size="is-small"
                      >
                      </b-icon>
                    </b-tooltip>
                    {{
                      pricingInfo.car.total
                        | formatPriceFromAmount(0, userDefaultCurrency)
                    }}
                  </span>
                </div>
              </div>
              <div v-if="pricingInfo.hotel">
                <br />
                <span
                  class="is-block is-size-6 has-text-weight-bold mb-2 segment-title-line"
                >
                  {{ pricingInfo.hotel.message }}
                </span>

                <!-- Base rate line -->
                <div class="is-flex is-justify-content-space-between">
                  <span class="is-size-6">
                    Base
                    <span v-if="pricingInfo.hotel.nights > 1" class="has-text-grey-light">
                      (average {{ pricingInfo.hotel.averageBaseNightlyRate | formatPriceFromAmount(0, userDefaultCurrency) }}/night)
                    </span>
                  </span>
                  <span class="is-size-6">
                    {{ pricingInfo.hotel.fare | formatPriceFromAmount(0, userDefaultCurrency) }}
                  </span>
                </div>

                <!-- Taxes line -->
                <div v-if="pricingInfo.hotel.tax" class="is-flex is-justify-content-space-between">
                  <span class="is-size-6 has-text-grey-light">
                    Taxes
                  </span>
                  <span class="is-size-6 is-pulled-right has-text-grey-light">
                    {{
                      pricingInfo.hotel.tax
                        | formatPriceFromAmount(0, userDefaultCurrency)
                    }} </span
                  ><br />
                </div>

                <div class="estimated-total-line">
                  <span class="is-size-6 estimated-total-line">
                    Estimated Total
                  </span>
                  <span class="is-size-6 is-pulled-right mb-2">
                    <b-tooltip
                      v-if="pricingInfo.hotel.isConversion"
                      class="is-size-5"
                      type="is-dark"
                      position="is-bottom"
                      multilined
                    >
                      <template v-slot:content>
                        {{ pricingInfo.hotel.currencyConvertedMessage }}
                      </template>
                      <b-icon
                        type="is-success"
                        icon="approximately-equal"
                        size="is-small"
                      >
                      </b-icon> </b-tooltip
                    >{{
                      pricingInfo.hotel.total
                        | formatPriceFromAmount(0, userDefaultCurrency)
                    }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          class="boxWithBorder mb-3"
          v-if="!pathMixin_isMeeting || this.useBookingTools"
        >
          <h3
            class="is-size-6 is-uppercase has-text-grey-darker has-text-weight-bold"
          >
            Trip Details
          </h3>
          <SmallItineraryPiece
            :sol="solutionAll"
            :key="
              flightSolution.key + solutionAll.hotel.key + solutionAll.car.key
            "
            :show-footer="false"
          />
        </div>

        <div class="boxWithBorder " v-if="modifyingTrip">
          <h3
            class="is-size-6 is-uppercase has-text-grey-darker has-text-weight-bold"
          >
            Current Itinerary
          </h3>

          <h3 class="is-size-7 is-italic has-text-grey">
            Existing reservations on this trip
          </h3>

          <PreviousTripItinerary :trip="trip" />
        </div>
      </div>
      <div
        class="column mr-3"
        :class="!pathMixin_isMeeting ? 'is-two-thirds' : ''"
      >
        <div v-if="!pathMixin_isMeeting">
          <Notes class="mb-3" />
        </div>
        <div
          class="pb-0 mb-0"
          :class="!pathMixin_isMeeting ? 'boxWithBorder' : 'box'"
        >
          <!-- TRAVELER INFORMATION -->

          <div>
            <h3
              class="is-size-6 is-uppercase has-text-grey-darker has-text-weight-bold"
            >
              <span class="is-pulled-left">Traveler Information</span>
            </h3>
            <div class="is-clearfix"></div>
          </div>
          <hr />

          <TravelerInformation
            v-model="profile"
            :label-position="labelPosition"
            :disabled="modifyingTrip"
          />

          <!-- Unaccompanied MINOR  -->
          <div class="mb-6" v-if="isUnaccompaniedMinor && !noTravelBooked">
            <h3
              class="is-size-6 is-uppercase has-text-grey-darker has-text-weight-bold"
            >
              Unaccompanied Minor
            </h3>
            <hr />
            <div class="mb-3">
              <p>
                Based on their date of birth and travel dates this traveler may
                be required to participate in the airline's unaccompanied minor
                program. Your booking request will be reviewed and processed by
                an agent and may result in additional service fees.
              </p>
              <p>
                <a
                  v-if="unaccompaniedMinorAirlineInfo.detailsUrl"
                  :href="unaccompaniedMinorAirlineInfo.detailsUrl"
                  target="_blank"
                  >View more details on the
                  {{ unaccompaniedMinorAirlineInfo.airlineName }} website</a
                >
              </p>
            </div>
            <b-field>
              <b-checkbox
                type="is-primary"
                v-model="unaccompaniedMinor.agreeToFees"
                >I have read and understand the Unaccompanied Minor policy
                stated above.</b-checkbox
              >
            </b-field>
            <b-field label="Parent / Guardian Name">
              <b-input
                v-model="unaccompaniedMinor.parentName"
                validation-message="Please enter a valid name"
                pattern="[a-zA-Z]*"
                required
              ></b-input>
            </b-field>
            <b-field label="Parent / Guardian Phone Number">
              <b-input
                v-model="unaccompaniedMinor.parentPhone"
                v-cleave="masks.phone"
                required
              ></b-input>
            </b-field>
          </div>

          <div v-if="!noTravelBooked">
            <!-- Hide if no travel in cart-->
            <!--  FREQUENT TRAVEL PROGRAMS  -->
            <template v-if="!isWebBedsHotel(pricingInfo.hotel.infoSource)">
              <h3 class="is-size-6 is-uppercase has-text-grey-darker has-text-weight-bold">Frequent Travel Programs</h3>
              <hr />
              <div class="pb-5" v-for="(program, index) in travelerPrograms" :key="index">
                <b-field
                  :label-position="labelPosition"
                  class="mb-5"
                  :message="isMemberRateProgram(program) ? 'A frequent traveler number is required to apply a member rate to your hotel booking.' : vendorMessage(program)"
                  :type="isMemberRateProgram(program) ? 'is-info' : ''"
                >
                  <template #label>
                    {{ program.providerName }}<span v-if="isMemberRateProgram(program)" class="has-text-danger">* </span>
                  </template>
                  <b-input
                    v-model="program.accountNumber"
                    :disabled="isProgramDisabled(program)"
                    pattern="^[A-Za-z0-9]+$"
                    validation-message="Please enter a valid program number using only letters and numbers."
                    maxlength="35"
                    :data-label="`${program.providerName} frequent travel number`"
                    pattern-validate
                    @blur="program.accountNumber = (program.accountNumber || '').trim()"
                  ></b-input>
                </b-field>
              </div>
            </template>
            <!-- GUEST TRAVELERS -->
            <div
              v-for="(guest, index) in guestTravelers"
              :key="guest.meetingAttendeeGuestId"
            >
              <div>
                <h3
                  class="is-size-6 is-uppercase has-text-grey-darker has-text-weight-bold"
                >
                  <span class="is-pulled-left"
                    >Guest Passenger #{{ index + 1 }}</span
                  >
                </h3>
                <div class="is-clearfix"></div>
              </div>
              <hr />

              <TravelerInformation
                :value="guest.profile"
                :label-position="labelPosition"
                :disabled="modifyingTrip"
                @input="updateGuestProfile"
              />

              <div v-if="!isWebBedsHotel(pricingInfo.hotel.infoSource)" class="mb-6">
                <h3
                  class="is-size-6 is-uppercase has-text-grey-darker has-text-weight-bold"
                >
                  Frequent Travel Programs
                </h3>
                <hr />
                <div class="pb-5" v-for="program in guest.travelerPrograms"
                  :key="program.providerCode"
                >
                  <b-field
                    :label-position="labelPosition"
                    class="mb-5"
                    :message="isMemberRateProgram(program) ? 'A frequent traveler number is required to apply a member rate to your hotel booking.' : ''"
                    :type="isMemberRateProgram(program) ? 'is-info' : ''"
                  >
                    <template #label>
                      {{ program.providerName }}<span v-if="isMemberRateProgram(program)" class="has-text-danger">* </span>
                    </template>
                    <b-input v-model="program.accountNumber" @blur="program.accountNumber = (program.accountNumber || '').trim()"></b-input>
                  </b-field>
                </div>
              </div>
            </div>

            <!-- PAYMENT DETAILS -->
            <div
              class="mb-6"
              v-if="
                !paymentMethodMeeting &&
                  !noTravelBooked &&
                  !disableSelectPaymentMethods
              "
            >
              <h3
                class="is-size-6 is-uppercase has-text-grey-darker has-text-weight-bold"
              >
                Your Payment Details
              </h3>
              <hr />

              <UserAirNotifications
                class="mt-3 mb-3"
                v-if="hasMatchingUnusedTicket"
              />

              <PaymentMethodNotifications :payments="payments" />
              <PaymentMethodCardGrid
                :payments="payments"
                :customPaymentChosen="customPaymentChosen"
                :selectedPayment="
                  selectedPayments.length > 0 ? selectedPayments[0] : null
                "
                @paymentSelected="selectPayments"
                v-if="pathMixin_isMeeting"
              />
              <ToggleSingleOrMultiPaymentMethod
                :payments="payments"
                :customPaymentChosen="customPaymentChosen"
                :pricingInfo="pricingInfo"
                :selectedPayment="
                  !useMultiplePaymentMethods && selectedPayments.length > 0
                    ? selectedPayments[0]
                    : null
                "
                :hotel="solutionAll.hotel"
                :car="carSegmentCreditCardRequired ? solutionAll.car : null"
                @paymentsSelected="selectPayments"
                @useMultiplePaymentMethods="toggleMultiplePayments"
                :loading="loadingPaymentOptions"
                v-if="!pathMixin_isMeeting"
              />

              <div v-if="allowNewPaymentMethod">
                <a class="teal-text" @click="customPaymentChosen = true"
                  ><u>+ Add a new card</u></a
                >
              </div>
            </div>
            <!--  COMPANY CARD IS PAYING  -->
            <div
              class="mb-6"
              v-else-if="!noTravelBooked && !disableSelectPaymentMethods"
            >
              <h3
                class="is-size-6 is-uppercase has-text-grey-darker has-text-weight-bold"
              >
                Payment Details
              </h3>
              <hr />
              A company card is paying for this trip.
            </div>

            <!-- DIRECT BILLING -->
            <div class="mb-6" v-if="carDirectBilingCompanyName">
              <h3
                class="is-size-6 is-uppercase has-text-grey-darker has-text-weight-bold"
              >
                Direct Billing
              </h3>
              <hr />
              <div class="mt-2">
                Direct billing is enabled for
                {{ carDirectBilingCompanyName }} under your company's travel
                program.
              </div>
            </div>

            <!-- OUT OF POLICY EXPLANATION -->
            <div class="mb-6" v-if="policyTriggered || displayApprovalBanner">
              <h3
                class="is-size-6 is-uppercase has-text-grey-darker has-text-weight-bold"
              >
                Travel Policy and Approvals
              </h3>
              <hr />

              <!-- Banner for All Trips Need Approval -->
              <div v-if="displayApprovalBanner">
                <div class="notification is-warning">
                  <b-icon icon="alert" size="is-small"></b-icon>
                  Your trip will be reviewed for approval.
                </div>
              </div>
              <hr />

              <!-- Out of Policy Explanation for Air -->
              <div
                v-if="
                  flightSolution.travelPolicy &&
                    flightSolution.travelPolicy.summary.rulesTriggeredCount != 0
                "
              >
                <p class="gant-text has-text-weight-bold mb-4">
                  Please explain why below booking(s) is/are out of policy.
                </p>
                <div class="content">
                  <ul>
                    <li
                      class="is-size-7"
                      style="padding-left: 10px;"
                      v-for="item in flightSolution.travelPolicy.rulesTriggered"
                      :key="`A ${item.message}`"
                    >
                      {{ item.message }}
                    </li>
                  </ul>
                </div>
                <CustomTextArea
                  v-model="policyNotes.air"
                  :maxlength="200"
                  placeholder="Notes for approver / logs"
                  pattern="^[A-Za-z0-9 .,+\-\(\)]+$"
                  :data-label="`air travel policy violation note`"
                  :validationMessage="allowedCharacterMsg"
                  class="mb-2"
                ></CustomTextArea>
              </div>

              <!-- Out of Policy Explanation for Hotel -->
              <div
                v-if="
                  solutionAll.hotel.travelPolicy &&
                    solutionAll.hotel.travelPolicy.summary
                      .rulesTriggeredCount != 0
                "
              >
                <p class="gant-text has-text-weight-bold mb-4">
                  Please explain why below booking(s) is/are out of policy.
                </p>
                <div class="content">
                  <ul>
                    <li
                      class="is-size-7"
                      style="padding-left: 10px;"
                      v-for="item in solutionAll.hotel.travelPolicy
                        .rulesTriggered"
                      :key="`B ${item.message}`"
                    >
                      {{ item.message }}
                    </li>
                  </ul>
                </div>
                <CustomTextArea
                  v-model="policyNotes.hotel"
                  :maxlength="200"
                  placeholder="Notes for approver / logs"
                  pattern="^[A-Za-z0-9 .,+\-\(\)]+$"
                  :data-label="`hotel travel policy violation note`"
                  :validationMessage="allowedCharacterMsg"
                  class="mb-2"
                ></CustomTextArea>
              </div>

              <!-- Out of Policy Explanation for Car -->
              <div
                v-if="
                  solutionAll.car.travelPolicy &&
                    solutionAll.car.travelPolicy.summary.rulesTriggeredCount !=
                      0
                "
              >
                <p class="gant-text has-text-weight-bold mb-4">
                  Please explain why below booking(s) is/are out of policy.
                </p>
                <div class="content">
                  <ul>
                    <li
                      class="is-size-7"
                      v-for="item in solutionAll.car.travelPolicy
                        .rulesTriggered"
                      :key="`C ${item.message}`"
                    >
                      {{ item.message }}
                    </li>
                  </ul>
                </div>
                <CustomTextArea
                  v-model="policyNotes.car"
                  :maxlength="200"
                  placeholder="Notes for approver / logs"
                  pattern="^[A-Za-z0-9 .,+\-\(\)]+$"
                  :data-label="`car travel policy violation note`"
                  :validationMessage="allowedCharacterMsg"
                ></CustomTextArea>
              </div>
            </div>

            <!-- REPORTING QUESTIONS -->
            <div
              class="mb-6"
              v-if="
                isBusinessTrip &&
                  filteredQuestions.length > 0 &&
                  !noTravelBooked &&
                  !modifyingTrip
              "
            >
              <h3
                class="is-size-6 is-uppercase has-text-grey-darker has-text-weight-bold"
              >
                Reporting Questions
              </h3>
              <hr />
              <div v-for="question in filteredQuestions" :key="question.id">
                <Questions v-model="question.answer" :udidQuestion="question" />
              </div>
            </div>

            <!-- OPTIONAL ITEMS -->
            <div class="mb-6">
              <h3
                class="is-size-6 is-uppercase has-text-grey-darker has-text-weight-bold"
              >
                Optional Items
              </h3>
              <hr />
              <b-collapse
                :open="false"
                aria-id="contentIdForA11y1"
                v-if="showNotesToAgent"
              >
                <template #trigger="props">
                  <b-button
                    type="is-text"
                    class="gant-text"
                    aria-controls="contentIdForA11y1"
                    :aria-expanded="props.open"
                  >
                    {{
                      props.open
                        ? "CANCEL: Special Request"
                        : "+ Add a Special Request to Agent"
                    }}
                  </b-button>
                </template>
                <div class="">
                  <div class="content mt-2 ">
                    <div class="mb-3">
                      Adding special requests will delay the processing of your
                      itinerary until your requests can be reviewed and
                      processed by an agent and may result in additional service
                      fees.
                    </div>
                    <div class="field">
                      <CustomTextArea
                        v-model="specialRequest.text"
                        :maxlength="200"
                        placeholder="Your Special Request"
                        pattern="^[A-Za-z0-9 .,+\-\(\)]+$"
                        :data-label="`special request`"
                        :validationMessage="allowedCharacterMsg"
                        class="mb-2"
                      ></CustomTextArea>
                    </div>
                  </div>
                </div>
              </b-collapse>
              <b-collapse
                v-if="!modifyingTrip"
                :open="false"
                aria-id="contentIdForA11y1"
              >
                <template #trigger="props">
                  <b-button
                    type="is-text"
                    class="gant-text"
                    aria-controls="contentIdForA11y1"
                    :aria-expanded="props.open"
                  >
                    {{
                      props.open
                        ? "CANCEL: Trip Description"
                        : "+ Add a Trip Name and Description"
                    }}
                  </b-button>
                </template>
                <div class="">
                  <div class="content mt-2 ">
                    <div class="field">
                      <b-field label="Name Your Trip (optional)">
                        <b-input
                          v-model="tripName"
                          maxlength="100"
                          pattern="^[A-Za-z0-9 .,+\-\(\)]+$"
                          data-label="trip name"
                          :validation-message="allowedCharacterMsg"
                          pattern-validate
                        ></b-input>
                      </b-field>
                    </div>
                    <div class="field">
                      <b-field label="Describe Your Trip (optional)">
                        <CustomTextArea
                          v-model="tripDescription"
                          :maxlength="100"
                          placeholder="Describe Your Trip (optional)"
                          pattern="^[A-Za-z0-9 .,+\-\(\)]+$"
                          :data-label="`trip description`"
                          :validationMessage="allowedCharacterMsg"
                          class="mb-2"
                        >
                        </CustomTextArea>
                      </b-field>
                    </div>
                  </div>
                </div>
              </b-collapse>
              <b-collapse
                v-if="!modifyingTrip"
                :open="false"
                aria-id="contentIdForA11y1"
              >
                <template #trigger="props">
                  <b-button
                    type="is-text"
                    class="gant-text"
                    aria-controls="contentIdForA11y1"
                    :aria-expanded="props.open"
                  >
                    {{
                      props.open
                        ? "CANCEL: Email Notifications"
                        : "+ Add Email Notifications"
                    }}
                  </b-button>
                </template>
                <div class="">
                  <div class="content mt-2 ">
                    <div class="mb-5">
                      A copy of the trip itinerary will be sent to:
                      <strong>{{ profile.email }}</strong>
                    </div>
                    <div class="field">
                      <b-field label="Notify Me" v-if="bookingForSomeoneElse">
                        <b-checkbox
                          type="is-primary"
                          v-model="ccEmailActiveUser"
                          >{{ activeUser.email }}</b-checkbox
                        >
                      </b-field>
                      <b-field
                        label="Additional Email Address to Notify (optional)"
                      >
                        <b-input type="email" v-model="ccEmail"></b-input>
                      </b-field>
                    </div>
                  </div>
                </div>
              </b-collapse>
            </div>

            <!-- Review Rules and Details -->
            <div
              class=""
              v-if="
                oneWayFlightBooked ||
                  solutionAll.hotel.hotelCode ||
                  solutionAll.car.searchBody
              "
            >
              <!-- <div class="box"> -->
              <h3
                class="is-size-6 is-uppercase has-text-grey-darker has-text-weight-bold"
              >
                Review Rules and Details
              </h3>
              <hr />
              <div class="is-flex is-align-content-center">
                <div class="field mr-1" v-if="oneWayFlightBooked">
                  <b-button
                    size="is-small"
                    type="is-warning"
                    @click="fareRulesModal(flightSolution.journeys)"
                    rounded
                    >View Flight Fare Rules</b-button
                  >
                </div>
                <div class="field mr-1">
                  <div v-if="solutionAll.hotel.hotelCode">
                    <b-button
                      size="is-small"
                      type="is-warning"
                      @click="
                        hotelPricingModal(
                          solutionAll.hotel,
                          solutionAll.hotel.session
                        )
                      "
                      rounded
                      >View Hotel Rate / Cancellation Details</b-button
                    >
                  </div>
                </div>
                <div class="field mr-1">
                  <div v-if="solutionAll.car.searchBody">
                    <b-button
                      size="is-small"
                      type="is-warning"
                      @click="
                        carRateModal(
                          solutionAll.car,
                          solutionAll.car.searchBody
                        )
                      "
                      rounded
                      >View Car Rate Details</b-button
                    >
                  </div>
                </div>
              </div>
            </div>

            <!--  PREVIEW NOTIFICATION -->
            <b-notification
              v-if="previewMode"
              type="is-default"
              class="mb-2 mt-2 has-text-info"
              :closable="false"
            >
              <strong>Heads up!</strong> You are in "{{ previewMode }}" preview
              mode. Your reservation will not be created.
            </b-notification>

            <!--  SANDBOX NOTIFICATION -->
            <b-notification
              v-else-if="sandboxMode"
              type="is-default"
              class="mb-2 mt-2 has-text-info"
              :closable="false"
            >
              <strong>Heads up!</strong> You are in sandbox mode. Your booking
              will be created in the Amadeus sandbox.
            </b-notification>
          </div>
          <div class="mb-4 mt-5">
            <!--  COMPLETE BOOKING BUTTONS -->
            <div class="bookingButtonContainer">
              <div class="buttonContainer">
                <div class="bookingButtons">
                  <div v-if="!noTravelBooked">
                    <b-button
                      size="is-medium"
                      type="is-gant"
                      class="is-hidden-touch"
                      icon-left="lock"
                      :disabled="checkout_pending || !allowConfirm"
                      @click="checkInputs()"
                      style="padding: 15px 40px;"
                      :style="
                        !pathMixin_isMeeting
                          ? 'margin:auto;'
                          : 'margin: auto;'
                      "
                      
                    >
                      <h5>
                        <span class="is-hidden-mobile"
                          >Complete My Booking</span
                        >
                      </h5>
                      <h5>
                        <span class="is-hidden-desktop is-hidden-tablet"
                          >Complete My Booking</span
                        >
                      </h5>
                    </b-button>
                    <b-button
                      size="is-medium"
                      type="is-gant"
                      expanded
                      icon-left="lock"
                      class="is-hidden-desktop"
                      :disabled="checkout_pending || !allowConfirm"
                      @click="checkInputs()"
                      style="margin:auto;"
                    >
                      <h5>
                        <span class="is-hidden-mobile"
                          >Complete My Booking</span
                        >
                      </h5>
                      <h5>
                        <span class="is-hidden-desktop is-hidden-tablet"
                          >Complete My Booking</span
                        >
                      </h5>
                    </b-button>
                  </div>
                  <div v-else-if="pathMixin_isMeeting">
                    <b-button
                      size="is-medium"
                      type="is-gant"
                      class="is-hidden-touch"
                      icon-right="lock"
                      :disabled="checkout_pending || !allowConfirm"
                      @click="confirmMeetingRegistration()"
                      style="margin:auto;"
                      expanded
                    >
                      <h5>Confirm Registration</h5>
                    </b-button>
                    <b-button
                      size="is-medium"
                      type="is-gant"
                      expanded
                      icon-right="lock"
                      class="is-hidden-desktop"
                      :disabled="checkout_pending || !allowConfirm"
                      @click="confirmMeetingRegistration()"
                      style="margin:auto;"
                    >
                      <h5>Confirm Registration</h5>
                    </b-button>
                  </div>
                  <!--                <p class="mb-4 mt-1 is-size-7 has-text-centered is-hidden-mobile is-hidden-tablet">-->
                  <!--                  <em>By submitting your booking request you agree to the terms and conditions associated with TripCircuit.-->
                  <!--                  </em></p>-->
                </div>
              </div>
              <!-- <img
                src="/images/SVGs/Checkout_group.svg"
                alt=""
                style="z-index: 10;"
              /> -->
            </div>

            <b-notification
              type="is-danger is-light"
              class="mt-5 my-3"
              v-if="inputErrors.length > 0"
              :closable="false"
            >
              <h4 class="is-size-5">Please correct the following errors:</h4>
              <div class="content">
                <ul>
                  <li
                    v-for="(validationError, index) in inputErrors"
                    :key="index"
                  >
                    {{ validationError }}
                  </li>
                </ul>
              </div>
            </b-notification>
          </div>
        </div>
        <!--  END OF COLUMN -->
      </div>
      <div class="column" v-if="isDesktop">
        <div v-if="isNDC">
          <b-notification
            :type="countdown > 0 ? 'is-warning' : 'is-danger'"
            :closable="false"
            position="is-top-right"
            has-icon
            icon="clock"
            icon-size="is-small"
          >
            <div v-if="countdown > 0" class="is-size-5 mt-2">
              Hurry! This fare expires in {{ minutes }} minutes
              {{ seconds }} seconds.
            </div>
            <div v-else class="is-size-5 mt-2">
              Sorry, this fare has expired.
            </div>
          </b-notification>

          <b-message
            title="Airline Direct (NDC) Fare"
            type="is-success"
            aria-close-label="Close message"
            class="mb-3"
          >
            <ul>
              <li>
                This booking contains a direct airline fare (NDC) and may
                contain special pricing.
              </li>
            </ul>
          </b-message>
        </div>
        <div
          class="mb-3"
          :class="!pathMixin_isMeeting ? 'boxWithBorder' : 'box'"
        >
          <div class="" v-if="!noTravelBooked">
            <div class="columns is-mobile is-vcentered">
              <div class="column is-7">
                <h3
                  class="is-size-6 is-uppercase has-text-grey-darker has-text-weight-bold"
                >
                  Total Trip Estimate
                </h3>
              </div>
              <div class="column is-4" style="text-align: right;">
                <h3 class="is-size-5 has-text-grey">
                  {{
                    pricingInfo.total
                      | formatPriceFromAmount(0, userDefaultCurrency)
                  }}
                </h3>
              </div>
              <div
                @click="priceExpanded = !priceExpanded"
                style="padding-left: 0"
                class="column is-1"
              >
                <b-icon
                  style="padding-top: 10px; cursor: pointer;"
                  icon="chevron-down"
                />
              </div>
            </div>
            <div v-if="priceExpanded">
              <hr />
              <div v-for="(flight, index) in pricingInfo.flight" :key="index">
                <span
                  class="is-block is-size-6 has-text-weight-bold my-2 segment-title-line"
                >
                  {{ flight.message }}
                </span>
                <span class="is-size-6">
                  Fare
                </span>
                <span class="is-block is-size-6 is-pulled-right">
                  {{
                    flight.fare | formatPriceFromAmount(0, userDefaultCurrency)
                  }} </span
                ><br />
                <div v-if="flight.seatFees > 0">
                  <span class="is-size-6 has-text-grey-light">
                    Seat Fees
                  </span>
                  <span class="is-size-6 is-pulled-right has-text-grey-light">
                    {{
                      flight.seatFees
                        | formatPriceFromAmount(0, userDefaultCurrency)
                    }} </span
                  ><br />
                </div>
                <span class="is-size-6 has-text-grey-light">
                  Taxes
                </span>
                <span class="is-size-6 is-pulled-right has-text-grey-light">
                  {{
                    flight.tax | formatPriceFromAmount(0, userDefaultCurrency)
                  }}
                </span>

                <div class="estimated-total-line">
                  <span class="is-size-6">
                    Estimated Total
                  </span>
                  <span class="is-size-6 is-pulled-right mb-2">
                    <!-- {{ pricingInfo.flight.total + seatPricing.total| formatPrice}} -->
                    {{
                      flight.total
                        | formatPriceFromAmount(0, userDefaultCurrency)
                    }}
                  </span>
                </div>
              </div>
              <div v-if="pricingInfo.car">
                <br />
                <span
                  class="is-block is-size-6 has-text-weight-bold mb-2 segment-title-line"
                >
                  {{ pricingInfo.car.message }}
                </span>

                <!-- Rental charge -->
                <div
                  class="is-flex is-justify-content-space-between is-align-items-center"
                >
                  <span class="is-size-6">
                    Rental charge ({{ pricingInfo.car.days }} days @
                    {{
                      pricingInfo.car.rate
                        | formatPriceFromAmount(0, userDefaultCurrency)
                    }}/day)
                  </span>
                  <span class="is-size-6">
                    {{
                      pricingInfo.car.fare
                        | formatPriceFromAmount(0, userDefaultCurrency)
                    }}
                  </span>
                </div>

                <!-- Taxes -->
                <div
                  v-if="pricingInfo.car.tax"
                  class="is-flex is-justify-content-space-between is-align-items-center"
                >
                  <span class="is-size-6 has-text-grey-light">
                    Taxes
                  </span>
                  <span class="is-size-6 has-text-grey-light">
                    {{
                      pricingInfo.car.tax
                        | formatPriceFromAmount(0, userDefaultCurrency)
                    }}
                  </span>
                </div>

                <!-- Estimated Total -->
                <div
                  class="is-flex is-justify-content-space-between is-align-items-center estimated-total-line"
                >
                  <span class="is-size-6">
                    Estimated Total
                  </span>
                  <span class="is-size-6 mb-2">
                    <b-tooltip
                      v-if="pricingInfo.car.isConversion"
                      class="is-size-5"
                      type="is-dark"
                      position="is-bottom"
                      multilined
                    >
                      <template v-slot:content>
                        {{ pricingInfo.car.currencyConvertedMessage }}
                      </template>
                      <b-icon
                        type="is-success"
                        icon="approximately-equal"
                        size="is-small"
                      ></b-icon>
                    </b-tooltip>

                    {{
                      pricingInfo.car.total
                        | formatPriceFromAmount(0, userDefaultCurrency)
                    }}
                  </span>
                </div>
              </div>

              <div v-if="pricingInfo.hotel">
                <br />
                <span
                  class="is-block is-size-6 has-text-weight-bold mb-2 segment-title-line"
                >
                  {{ pricingInfo.hotel.message }}
                </span>

                <!-- Base rate line -->
                <div class="is-flex is-justify-content-space-between is-align-items-center">
                  <span class="is-size-6">
                    Base 
                    <span v-if="pricingInfo.hotel.nights > 1">
                      ({{pricingInfo.hotel.nights}} nights @ {{ pricingInfo.hotel.averageBaseNightlyRate | formatPriceFromAmount(0, userDefaultCurrency) }}/night)
                    </span>
                  </span>
                  <span class="is-size-6">
                    {{
                      pricingInfo.hotel.fare
                        | formatPriceFromAmount(0, userDefaultCurrency)
                    }}
                  </span>
                </div>

                <!-- Taxes -->
                <div
                  v-if="pricingInfo.hotel.tax"
                  class="is-flex is-justify-content-space-between is-align-items-center"
                >
                  <span class="is-size-6 has-text-grey-light">
                    Taxes
                  </span>
                  <span class="is-size-6 has-text-grey-light">
                    {{
                      pricingInfo.hotel.tax
                        | formatPriceFromAmount(0, userDefaultCurrency)
                    }}
                  </span>
                </div>

                <!-- Estimated Total -->
                <div
                  class="is-flex is-justify-content-space-between is-align-items-center estimated-total-line"
                >
                  <span class="is-size-6">
                    Estimated Total
                  </span>
                  <span class="is-size-6 mb-2">
                    <b-tooltip
                      v-if="pricingInfo.hotel.isConversion"
                      class="is-size-5"
                      type="is-dark"
                      position="is-bottom"
                    >
                      <template v-slot:content>
                        {{ pricingInfo.hotel.currencyConvertedMessage }}
                      </template>
                      <b-icon
                        type="is-success"
                        icon="approximately-equal"
                        size="is-small"
                      ></b-icon>
                    </b-tooltip>

                    {{
                      pricingInfo.hotel.total
                        | formatPriceFromAmount(0, userDefaultCurrency)
                    }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          class="mb-3"
          :class="!pathMixin_isMeeting ? 'boxWithBorder' : 'box'"
          v-if="!pathMixin_isMeeting || this.useBookingTools"
        >
          <h3
            class="is-size-6 is-uppercase has-text-grey-darker has-text-weight-bold"
          >
            Trip Details
          </h3>
          <SmallItineraryPiece
            :sol="solutionAll"
            :guests="$store.state.bookTravel.guests"
            :show-footer="false"
          />
          <!--
            <GuestItinerary
              v-for="(guest, index) in $store.state.bookTravel.guests"
              :key="index"
              :solution="guest.result"
              :traveler="guest.profile"
              :show-footer="false"
            />
            -->
        </div>

        <div class="boxWithBorder " v-if="modifyingTrip">
          <h3
            class="is-size-6 is-uppercase has-text-grey-darker has-text-weight-bold"
          >
            Current Itinerary
          </h3>

          <h3 class="is-size-7 is-italic has-text-grey">
            Existing reservations on this trip
          </h3>

          <PreviousTripItinerary :trip="trip" />
        </div>
      </div>
    </div>

    <b-modal
      v-model="checkout_pending"
      has-modal-card
      trap-focus
      :destroy-on-hide="true"
      aria-role="dialog"
      aria-label="PNR Pending"
      aria-modal
      :canCancel="[]"
    >
      <template>
        <PNRPending
          v-if="checkout_pending"
          :key="pending_pnr.bookingKey"
          :pnrData="pending_pnr"
          @close="pnrComplete"
          @complete="pnrComplete"
        />
      </template>
    </b-modal>

    <b-modal
      v-model="customPaymentChosen"
      has-modal-card
      trap-focus
      :destroy-on-hide="false"
      aria-role="dialog"
      aria-label="Add card Modal"
      aria-modal
      :canCancel="['escape', 'outside', 'x']"
    >
      <template #default="props">
        <AddCardModal @close="props.close" @update="addCard"></AddCardModal>
      </template>
    </b-modal>
  </div>
</template>

<script>
import moment from "moment"
import Cleave from "cleave.js"
const cleave = {
  name: "cleave",
  bind(el, binding) {
    const input = el.querySelector("input")
    input._vCleave = new Cleave(input, binding.value)
  },
  unbind(el) {
    const input = el.querySelector("input")
    input._vCleave.destroy()
  },
}
import modalMixin from "../../mixins/modalMixin"
import bookingMixin from "../../mixins/bookingMixin"
import meetingsMixin from "../../mixins/meetingsMixin"
import SmallItineraryPiece from "../Itinerary/SmallItineraryPiece"
import Questions from "../Itinerary/Questions"
import PNRPending from "../Itinerary/PNR_Pending"
import AddCardModal from "../CreditCardEntry/AddCardModal"
import UserAirNotifications from "../User/UserAirNotifications"
import PreviousTripItinerary from "@/components/Trip/PreviousTripItinerary"
import TravelerInformation from "@/components/TravelerInformation"
import CustomTextArea from "@/components/FormComponents/CustomTextArea"
import PaymentMethodCardGrid from "@/components/BookTravel/PaymentMethodCardGrid"
import ToggleSingleOrMultiPaymentMethod from "@/components/BookTravel/ToggleSingleOrMultiPaymentMethod"
import PaymentMethodNotifications from "@/components/BookTravel/PaymentMethodNotifications"
import Notes from "@/components/UI/Notes.vue"

export default {
  name: "BookingCheckout",
  directives: {
    cleave,
  },
  mixins: [modalMixin, bookingMixin, meetingsMixin],
  components: {
    SmallItineraryPiece,
    Questions,
    PNRPending,
    AddCardModal,
    UserAirNotifications,
    PreviousTripItinerary,
    TravelerInformation,
    CustomTextArea,
    PaymentMethodCardGrid,
    ToggleSingleOrMultiPaymentMethod,
    PaymentMethodNotifications,
    Notes,
  },

  data: function() {
    return {
      checkout_pending: false,
      countdown: 0,
      priceExpanded: false,
      inputErrors: [],
      unaccompaniedMinor: {},
      unaccompaniedMinorAirlineDetailsUrl: null,
      travelerPrograms: [],
      masks: {
        creditCard: { creditCard: true },
        numeral: {
          numeral: true,
          numeralThousandsGroupStyle: "thousand",
          prefix: "$ ",
        },
        date: {
          date: true,
          datePattern: ["m", "y"],
        },
        dob: {
          date: true,
          delimiter: "/",
          datePattern: ["m", "d", "Y"],
        },
        phone: {
          phone: true,
          phoneRegionCode: "US",
          delimiter: "-",
        },
      },
      customPaymentChosen: false,
      /*
        formProps: {
          req: this.$store.state.pnr.pnr.req,
        },
        */
      first_name: "",
      tripName: "",
      tripDescription: "",
      allowSpecialRequests: true,
      specialRequest: {
        activated: false,
        text: "",
      },
      loading: true,
      //profile: {},
      policyNotes: { air: "", hotel: "", car: "" },
      last_name: "",
      customPayment: {
        cardholderName: "",
        expDate: { month: "", year: "" },
        cardNum: "",
      },
      selectedPayments: [],
      selectedTicket: "",
      checkedPrice: null,
      hover: false,
      value: "",
      priceChanged: false,
      rawValue: "",
      // questions: [{question: "What is the purpose of this trip?", options: ['Personal','Corporate'], type:"mc"},
      //             {question: "Additional Comments", options: null, type:"open"}],
      questions: [],
      ccEmailActiveUser: false,
      ccEmail: null,
      labelPosition: "on-border",
      isDesktop: true,
      allowedCharacterMsg:
        "Please remove characters that are not letters, numbers, spaces, commas, periods, plus signs, hyphens, or parentheses.",
      cartContentForEventTracking: null,
      useMultiplePaymentMethods: false,
      loadingPaymentOptions: true,
      carDirectBilingCompanyName: null,
    }
  },
  computed: {
    profile: {
      get() {
        return this.$store.getters[
          "travellersAndTrips/getCurrentTravellerProfile"
        ]
      },
      set(value) {
        this.$store.commit("travellersAndTrips/SET_TRAVELLER_PROFILE", value)
      },
    },
    minutes() {
      return Math.floor(this.countdown / 60)
    },
    seconds() {
      return this.countdown % 60
    },
    isNDC() {
      if (this.selectedOffer) {
        return true
      } else {
        return false
      }
    },
    selectedOffer() {
      return this.$store.state.bookTravel.result.selectedOffer
    },
    flightSolution() {
      return this.$store.state.bookTravel.result.flight
    },
    noTravelBooked() {
      return (
        this.$helpers.isEmptyObject(this.flightSolution) &&
        this.$helpers.isEmptyObject(this.solutionAll.hotel) &&
        this.$helpers.isEmptyObject(this.solutionAll.car)
      )
    },
    previewMode() {
      return this.$appConfig.previewMode // options: null, 'success', 'failure', 'partial';
    },
    sandboxMode() {
      return this.$appConfig.amadeusEnvironment == "sandbox" // options: null, 'success', 'failure', 'partial';
    },
    pricingInfo() {
      var price = { hotel: false, car: false, flight: false, total: 0 }
      if (this.selectedCar) {
        price.car = {}
        if (
          this.solutionAll.car.pickupInfo.locationKey ==
          this.solutionAll.car.dropoffInfo.locationKey
        ) {
          price.car.message =
            "Car rental in " + this.solutionAll.car.pickupLocation.city
        } else {
          price.car.message =
            "Car rental (" +
            this.solutionAll.car.pickupLocation.city +
            ", " +
            this.solutionAll.car.dropoffLocation.city +
            ")"
        }

        price.car.isConversion = false

        if (this.solutionAll.car.sourcePricingInfo?.currencyCode) {
          const sourceCurrency = this.solutionAll.car.sourcePricingInfo
            .currencyCode
          const requestedCurrency = this.solutionAll.car.pricingInfo
            .currencyCode
          if (sourceCurrency !== requestedCurrency) {
            price.car.isConversion = true
          }
        }

        price.car.total = this.solutionAll.car.pricingInfo.total
        price.car.days = this.solutionAll.car.tripLength
        price.car.rate = Math.round(price.car.total / price.car.days)
        price.car.fare = price.car.total
        price.car.currencyConvertedMessage =
          "Approximate Total - The currency has been converted from " +
          this.solutionAll.car.sourcePricingInfo.total +
          " " +
          this.solutionAll.car.sourcePricingInfo.currencyCode +
          " to " +
          price.car.total +
          " " +
          this.solutionAll.car.pricingInfo.currencyCode
        price.total += price.car.total
      }
      if (this.selectedAirlines.length > 0) {
        price.flight = []
        price.flight.push(this.calculateFlightCost(price, this.solutionAll))
        this.guestTravelers.forEach((guest) => {
          price.flight.push(this.calculateFlightCost(price, guest.result))
        })
        price.total += price.flight.reduce((sum, i) => sum + i.total, 0)
      }
      if (this.selectedHotel) {
        price.hotel = {}
        price.hotel.message =
          "Hotel Stay in " + this.solutionAll.hotel.hotelCity
        price.hotel.isConversion = false
        price.hotel.infoSource = this.solutionAll.hotel?.infoSource;
        const totalRequestedCurrency = this.solutionAll.hotel
          .requestedCurrencyPricing.total
        const nights = this.solutionAll.hotel.nights

        let taxHotel = 0
        let fareHotel = totalRequestedCurrency
        let rateHotel = totalRequestedCurrency / nights

        if (
          this.solutionAll.hotel.requestedCurrencyPricing.amountAfterTax &&
          this.solutionAll.hotel.requestedCurrencyPricing.amountBeforeTax
        ) {
          const amountAfterTax = parseFloat(
            this.solutionAll.hotel.requestedCurrencyPricing.amountAfterTax
          )
          const amountBeforeTax = parseFloat(
            this.solutionAll.hotel.requestedCurrencyPricing.amountBeforeTax
          )
          const totalOriginal = parseFloat(
            this.solutionAll.hotel.requestedCurrencyPricing.total
          )

          taxHotel =
            (amountAfterTax - amountBeforeTax) *
            (totalRequestedCurrency / totalOriginal)
          fareHotel = totalRequestedCurrency - taxHotel
          rateHotel = fareHotel / nights
        }

          if(this.solutionAll.hotel.sourceCurrencyPricing.currencyCode !== this.solutionAll.hotel.requestedCurrencyPricing.currencyCode){
            price.hotel.isConversion = true;
          }
  
          price.hotel.total = totalRequestedCurrency;
          price.hotel.averagePrice = this.solutionAll.hotel.requestedCurrencyPricing.dailyRate;
          price.hotel.averageBaseNightlyRate = this.solutionAll.hotel.requestedCurrencyPricing.baseNightlyRate || rateHotel;
          price.hotel.nights = nights;
          price.hotel.isConversion = this.solutionAll.hotel.sourceCurrencyPricing.currencyCode != this.solutionAll.hotel.requestedCurrencyPricing.currencyCode;
          price.hotel.currencyConvertedMessage =
            "Approximate Total - The currency has been converted from " +
            this.solutionAll.hotel.sourceCurrencyPricing.total +
            " " +
            this.solutionAll.hotel.sourceCurrencyPricing.currencyCode +
            " to " +
            totalRequestedCurrency +
            " " +
            this.solutionAll.hotel.requestedCurrencyPricing.currencyCode;
          price.hotel.tax = taxHotel;
          price.hotel.fare = fareHotel;
          price.hotel.rate = rateHotel;
          price.total += totalRequestedCurrency;
        }
        return price;
      },
      //TO DO: Refactor pricingInfo, findRelevantPrograms, selectedAirlines, selectedHotel, selectedCar to account guest travelers in the same checkout
      selectedAirlines() {
        var airlines = [];
        if (!this.flightSolution.journeys) {
          return [...new Set(airlines)];
        }
        var journey;
        var leg;
        for (journey of this.flightSolution.journeys) {
          for (leg of journey.legs) {
            airlines.push(leg.marketingAirline.code);
          }
        }
        return [...new Set(airlines)];
      },
      selectedHotel() {
        if (this.solutionAll.hotel) {
          return this.solutionAll.hotel.chainCode;
        }
        return null;
      },
      selectedCar() {
        if (this.solutionAll.car.companyInfo) {
          return this.solutionAll.car.companyInfo.code;
        }
        return null;
      },
      nothingBooked() {
        let solution = this.$store.state.bookTravel.result;
        return (
          Object.keys(solution.flight).length +
            Object.keys(solution.car).length +
            Object.keys(solution.hotel).length ===
          0
        );
      },
      booking_queue() {
        return this.$store.state.pnr.queue;
      },
      /*
      pending_pnr() {
        return this.$store.state.pnr.pending;
      },
      */

    //MT Can I refactor pending_pnr as below and remove the SET_PNR_PENDING Vuex mutation?
    // Current way seems to cause a bug where timed out/re-try bookings are not removed,
    // causing next bookings to appear to fail even when successful.
    pending_pnr() {
      return this.booking_queue[0]
    },

    /*pnr_is_pending() {
        return Object.keys(this.pending_pnr).length !== 0;
      },
      */
    allowConfirm() {
      return true
    },
    isUnaccompaniedMinor() {
      // No flight, return false
      if (!this.flightSolution) return false

      // No flight journeys
      if (this.flightSolution.journeys === undefined) return false

      // Get the date of the first flight
      let dateOfFirstFlight = this.flightSolution.journeys[0].startDate

      let airlineDetails = this.unaccompaniedMinorAirlineInfo

      let absoluteMinAge = airlineDetails.ageWhereRequired.min
      let absoluteMaxAge = airlineDetails.ageWhereRequired.max

      if (airlineDetails.ageWhereOptional.max) {
        absoluteMaxAge = airlineDetails.ageWhereOptional.max
      }

      let dateOfBirth = null
      let firstTravelDate = null

      if (dateOfFirstFlight) {
        firstTravelDate = moment(dateOfFirstFlight)
      }

      // Get traveler's date of birth
      if (this.profile.dateOfBirth) {
        dateOfBirth = moment(this.profile.dateOfBirth).format("MM-DD-YYYY")
      }

      // Check traveler's age on date of first flight
      if (dateOfBirth && firstTravelDate) {
        let ageInYears = firstTravelDate.diff(dateOfBirth, "years")

        if (ageInYears >= absoluteMinAge && ageInYears <= absoluteMaxAge)
          return true
      }

      return false
    },
    unaccompaniedMinorAirlineInfo() {
      // Set defaults
      let airlineDetails = {
        ageWhereRequired: {
          min: 5,
          max: 14,
        },
        ageWhereOptional: {
          min: null,
          max: null,
        },
        airlineName: "airline",
        detailsUrl: null,
      }

      // No flight, return false
      if (!this.flightSolution) return airlineDetails

      // No flight journeys
      if (this.flightSolution.journeys === undefined) return airlineDetails

      let airlineCode = this.flightSolution.journeys[0].airlineCode

      let unaccompaniedMinorAirlineDetails = this.$store.state
        .travellersAndTrips.unaccompaniedMinorAirlineDetails

      let matchingAirlineDetails = unaccompaniedMinorAirlineDetails.filter(
        (item) => {
          return item.airlineCode == airlineCode
        }
      )

      if (matchingAirlineDetails.length == 1) {
        airlineDetails = matchingAirlineDetails[0]
      }

      return airlineDetails
    },
    /*travelPrograms() {
        return this.$store.getters[
          "travellersAndTrips/getCurrentTravellerProfile"
        ].travelerPrograms;
      },*/
    paymentMethodMeeting() {
      if (this.$store.state.meetings.meetingInfo) {
        return this.$store.state.meetings.meetingInfo.meeting.paymentMethodId
      }

      return null
    },
    allowNewPaymentMethod() {
      let tripType = this.$store.getters["travellersAndTrips/getTravellers"]
        .tripType

      if (tripType == "personal") return true

      if (
        tripType == "personal" ||
        this.$store.getters["travellersAndTrips/getCurrentTravellerProfile"]
          .travelPolicy == null
      )
        return true

      if (
        tripType != "personal" &&
        this.$store.getters["travellersAndTrips/getCurrentTravellerProfile"]
          .travelPolicy != null &&
        this.$store.getters["travellersAndTrips/getCurrentTravellerProfile"]
          .travelPolicy.allowPersonalPaymentMethods == true
      )
        return true

      // If in meeting mode and no payment method on the meeting
      // TODO: also handle the case where the payment method is configured at the meeting attendee level
      if (this.pathMixin_isMeeting && this.paymentMethodMeeting == null) {
        return true
      }

      return false
    },
    payments() {
      let cards = []
      let personalCards = this.$store.getters[
        "travellersAndTrips/getCurrentTravellerProfile"
      ].paymentMethods

      cards = this.$store.getters["bookTravel/getGlobalPaymentOptions"]
      // Meeting mode, without a payment method set for the meeting as whole
      if (this.pathMixin_isMeeting && this.paymentMethodMeeting == null) {
        cards = personalCards
      }

      //remove duplicates
      return this.$helpers.removeDuplicates(cards, "id")
    },
    hasMatchingUnusedTicket() {
      var hasMatch = false
      var airlineCodes = this.$store.getters[
        "travellersAndTrips/getCurrentTravellerProfile"
      ].unusedTicketAirlines
      if (airlineCodes) {
        for (let airlineCode of airlineCodes) {
          if (this.selectedAirlines.includes(airlineCode)) {
            hasMatch = true
          }
        }
      }
      return hasMatch
    },
    policyTriggered() {
      var rules = 0
      if (this.solutionAll.car.travelPolicy) {
        rules += this.solutionAll.car.travelPolicy.summary.rulesTriggeredCount
      }
      if (this.solutionAll.hotel.travelPolicy) {
        rules += this.solutionAll.hotel.travelPolicy.summary.rulesTriggeredCount
      }
      if (this.flightSolution.travelPolicy) {
        rules += this.flightSolution.travelPolicy.summary.rulesTriggeredCount
      }
      return rules > 0
    },
    traveller() {
      return this.$store.getters[
        "travellersAndTrips/getCurrentTravellerProfile"
      ]
    },
    activeUser() {
      return this.$store.getters["app/getActiveUser"]
    },
    bookingForSomeoneElse() {
      return this.activeUser.id != this.traveller.id
    },
    oneWayFlightBooked() {
      if (this.flightSolution.journeys) {
        return this.flightSolution.journeys.length == 1
      }
      return false
    },
    isBusinessTrip() {
      return this.tripType == "business"
    },
    seatPricing() {
      try {
        var flight = this.$store.state.bookTravel.result.flight
        var seatPrice = []
        var total = 0
        for (var journey of flight.journeys) {
          for (var leg of journey.legs) {
            if (leg.selectedSeat) {
              if (leg.selectedSeat.price) {
                total += leg.selectedSeat.price.total
                seatPrice.push(leg.selectedSeat)
              }
            }
          }
        }
        return { seats: seatPrice, total: total }
      } catch {
        return { seats: [], total: 0 }
      }
    },
    /*
      req() {
        return this.$store.state.pnr.pnr.req;
      },
      */
    tripType() {
      let tripType = this.$store.getters["travellersAndTrips/getTravellers"]
        .tripType

      return tripType
    },
    isPersonalTrip() {
      return this.tripType == "personal"
    },
    guestTravelers() {
      return this.$store.state.bookTravel.guests
    },
    dobFormatted() {
      return moment(this.profile.dateOfBirth).format("MM/DD/YYYY")
    },
    filteredQuestions() {
      let questions = this.questions
      let filteredQuestions = []

      if (this.pathMixin_isMeeting) {
        filteredQuestions = questions.filter((question) => {
          return question.showInTripCircuitMeetings
        })
      } else {
        filteredQuestions = questions.filter((question) => {
          return question.showInTripCircuitNonMeetings
        })
      }

      return filteredQuestions
    },
    showNotesToAgent() {
      const disableNotesToAgent = this.$store.getters[
        "travellersAndTrips/getCurrentTravellerProfile"
      ]?.account?.disableNotesToAgent

      if (disableNotesToAgent) return false

      return true
    },
    allAirTripsNeedApproval() {
      return this.traveller?.travelPolicy?.allAirTripsNeedApproval
    },
    allCarTripsNeedApproval() {
      return this.traveller?.travelPolicy?.allCarTripsNeedApproval
    },
    allHotelTripsNeedApproval() {
      return this.traveller?.travelPolicy?.allHotelTripsNeedApproval
    },
    displayApprovalBanner() {
      // Display the approval banner even if policy is not triggered, based on the conditions
      return (
        (this.flightSolution && this.allAirTripsNeedApproval) ||
        (this.solutionAll.car && this.allCarTripsNeedApproval) ||
        (this.solutionAll.hotel && this.allHotelTripsNeedApproval)
      )
    },
    flightCurrency() {
      return this.flightSolution?.pricing?.currencyCode || this.userDefaultCurrency
    },
    userDefaultCurrency() {
      return this.$store.getters["travellersAndTrips/getUserDefaultCurrency"]
    },
    disableSelectPaymentMethods() {
      // Disable payment method selection if the trip is only for car rental
      // UNLESS the car requires a credit card guarantee
      const isCarOnly = (
        this.bookTrip.currentlyBooking == true &&
        this.bookTrip?.flight === false &&
        this.bookTrip?.car === true &&
        this.bookTrip?.hotel === false
      );

      if (!isCarOnly) {
        return false;
      }

      // If car only, check if credit card is required for guarantee
      return !this.carSegmentCreditCardRequired;
    },
    carSegmentCreditCardRequired() {
      // Check if car segment requires credit card for guarantee
      if (this.solutionAll?.car?.guaranteeInfo) {
        const guaranteeInfo = this.solutionAll.car.guaranteeInfo;
        return guaranteeInfo.isRequired && guaranteeInfo.creditCardOnly;
      }
      return false;
    },
    mappedPaymentMethods() {
      if (this.selectedPayments && this.selectedPayments.length > 0) {
        return this.selectedPayments.map((payment) => {
          return {
            id: payment.id,
            segments: payment.segments || [],
            //amount: payment.amount || null
          }
        })
      } else {
        return []
      }
    },
  },

  watch: {
    selectedPayments(newVal, oldVal) {
      if (newVal && newVal.length) {
        // Clear out error messages when a payment method is added
        this.inputErrors = []
      }
    },
    bookingForSomeoneElse(newVal) {
      if (newVal) {
        this.ccEmailActiveUser = true
      } else {
        this.ccEmailActiveUser = false
      }
    },
  },
  methods: {
    /*
      closePnrPendingModal(result) {
        //delete pending pnr
        //this.$store.commit("pnr/SET_PNR_PENDING", {});
        this.$store.commit("pnr/DEL_PNR_FROM_QUEUE", result.bookingKey);
        this.checkout_pending = false;
      },
      */
    updateGuestProfile(profile) {
      this.$store.dispatch("bookTravel/updateGuestProfile", {
        guest: this.profile,
        index: this.guestIndex,
      })
    },
    countdownTimer() {
      if (this.countdown > 0) {
        setTimeout(() => {
          this.countdown -= 1
          this.countdownTimer()
        }, 1000)
      }
    },
    getAirportLocation(airportCode) {
      let airport = this.$globals.airports.find(
        ({ code }) => code == airportCode
      )
      if (!airport) return null
      if (airport.state) return airport.city + ", " + airport.state
      else return airport.city + ", " + airport.country
    },
    confirmMeetingRegistration() {
      this.$router.push({ path: "/meetings/confirmation" })
    },
    checkInputs() {
      let messages = []
      if (
        this.selectedPayments.length === 0 &&
        !this.paymentMethodMeeting &&
        !this.disableSelectPaymentMethods
      ) {
        messages.push("Please select a payment method")
      }

      if (this.useMultiplePaymentMethods && !this.paymentMethodMeeting) {
        const totalSegments = this.selectedPayments.reduce((sum, payment) => {
          return sum + (payment.segments ? payment.segments.length : 0)
        }, 0)

        if (totalSegments < 2) {
          messages.push("Please select an additional payment method.")
        }
      }

      if (
        !(
          this.profile.firstName &&
          this.profile.lastName &&
          this.profile.dateOfBirth &&
          this.profile.mobilePhone &&
          this.profile.email &&
          this.profile.gender
        )
      ) {
        messages.push("Please enter missing passenger details")
      }

      if (!this.profile.dateOfBirth) {
        messages.push("Date of Birth is required")
      }

      if (!this.$date.dobValidate(this.profile.dateOfBirth)) {
        messages.push("Date of birth seems to be in the wrong format")
      }

      if (this.checkUdids() > 0 && !this.modifyingTrip) {
        messages.push("Please answer required reporting questions")
      }

      if (this.isUnaccompaniedMinor && !this.unaccompaniedMinor.agreeToFees) {
        messages.push(
          "Please indicate you have read and understand the Unaccompanied Minor policy"
        )
      }

      if (this.isUnaccompaniedMinor && !this.unaccompaniedMinor.parentName) {
        messages.push(
          "Please provide a parent or guardian name in the Unaccompanied Minor section"
        )
      }

      if (this.isUnaccompaniedMinor && !this.unaccompaniedMinor.parentPhone) {
        messages.push(
          "Please provide a parent or guardian phone number in the Unaccompanied Minor section"
        )
      }

      if(this.solutionAll.hotel && this.solutionAll.hotel.isMemberRate) {
        const hotelProgram = this.travelerPrograms.find((program) => {
          return program.type == 'hotel' 
        });
        if(!hotelProgram || !hotelProgram.accountNumber) {
          messages.push("Please add a membership number for the selected hotel or select a different rate.")
        }
      }

      this.validatePatterns(messages) // Validate inputs w/regex patterns before proceeding

      if (messages.length > 0) {
        this.inputErrors = messages
      } else {
        this.submitCheckout()
      }
    },
    validatePatterns(messages) {
      const inputs = this.$el.querySelectorAll("[pattern-validate]")

      for (let i = 0; i < inputs.length; i++) {
        const input = inputs[i]
        const value = input.value.trim()

        if (value === "") {
          continue
        }

        const pattern = new RegExp(input.getAttribute("pattern"))
        if (!pattern.test(value)) {
          const label = input.getAttribute("data-label") || "form field"
          messages.push(
            `Please remove any invalid characters from your ${label}`
          )
        }
      }
    },
    calculateFlightCost(cost, solution) {
      cost = {}

      // Calculate seat fees
      cost.seatFees = 0
      for (var i = 0; i < solution.flight.journeys.length; i++) {
        for (var j = 0; j < solution.flight.journeys[i].legs.length; j++) {
          if (solution.flight.journeys[i].legs[j].selectedSeat) {
            if (solution.flight.journeys[i].legs[j].selectedSeat.price) {
              cost.seatFees +=
                solution.flight.journeys[i].legs[j].selectedSeat.price.total
            }
          }
        }
      }

      // Build message text
      if (solution.flight.journeys.length == 1) {
        cost.message =
          "Flight (" +
          solution.flight.originAirportCode +
          ", " +
          solution.flight.destinationAirportCode +
          ")"
      } else if (solution.flight.journeys.length == 2) {
        cost.message =
          "Round Trip Flight (" +
          solution.flight.originAirportCode +
          ", " +
          solution.flight.destinationAirportCode +
          ")"
      } else {
        cost.message = "Multi-City Flight"
      }

      let priceData = solution.flight.pricing

      // Calculate total flight cost
      cost.tax = priceData.tax
      cost.fare = priceData.fare
      cost.total = priceData.total
      cost.total += cost.seatFees

      return cost
    },
    //search traveler's programs for those that match the air/car/hotel vendors used in the booking
    findRelevantPrograms(travelerPrograms = []) {
      let programs = []

      // Air
      for (let airlineCode of this.selectedAirlines) {
        let program = {
          type: "air",
          providerCode: airlineCode,
          providerName: this.$globals.vendors.airlines[airlineCode],
          accountNumber: null,
        }

        let matchingProgram = travelerPrograms.find((i) => {
          return i.providerCode == airlineCode
        })

        if (matchingProgram) {
          program.accountNumber = (matchingProgram.accountNumber || '').trim()
        }

        programs.push(program)
      }
      // Car
      if (this.selectedCar) {
        let program = {
          type: "car",
          providerCode: this.selectedCar,
          providerName: this.$globals.vendors.carProviders[this.selectedCar],
          accountNumber: null,
        }

        let matchingProgram = travelerPrograms.find((i) => {
          return i.providerCode == this.selectedCar
        })

        if (matchingProgram) {
          program.accountNumber = (matchingProgram.accountNumber || '').trim()
        }

        programs.push(program)
      }
      // Hotel
      if (this.selectedHotel) {
        let program = {
          type: "hotel",
          providerCode: this.selectedHotel,
          providerName: this.$globals.vendors.hotelChains[this.selectedHotel],
          accountNumber: null,
        }

        let existingPrograms = travelerPrograms.filter((i) => {
          // If the frequent traveler program has a different chain code use those details instead
          // Example: HHonors membership for Hilton will apply to variety of different Hilton properties

          if (i.relatedProviderCodes) {
            if (i.relatedProviderCodes.includes(this.selectedHotel)) {
              program.parentProviderCode = i.providerCode
              program.providerCode = i.providerCode
              program.providerName =
                this.$globals.vendors.hotelChains[this.selectedHotel] +
                " (" +
                this.$globals.vendors.hotelChains[i.providerCode] +
                ")"
            }

            return (
              i.providerCode == this.selectedHotel ||
              i.relatedProviderCodes.includes(this.selectedHotel)
            )
          }
        })

        // No existing loyalty program found then check if the hotel is a brand of a parent group.
        if (existingPrograms.length === 0) {
          const hotelBrandRelationships = this.$globals.vendors.hotelBrandRelationships || []
          const parentGroup = hotelBrandRelationships.find(
            (group) => group.relatedBrands && group.relatedBrands.some((brand) => brand.code === this.selectedHotel)
          )
          if (parentGroup) {
            program.parentProviderCode = parentGroup.code
            program.providerCode = parentGroup.code
            program.providerName =
              this.$globals.vendors.hotelChains[this.selectedHotel] +
              " (" +
              parentGroup.name +
              ")"
          }
        } else {
          program.accountNumber = (existingPrograms[0].accountNumber || '').trim()
        }

        programs.push(program)
      }
      return programs
    },
    checkUdids() {
      var unanswered = 0
      for (let i = 0; i < this.filteredQuestions.length; i++) {
        if (
          this.filteredQuestions[i].isRequired &&
          (!this.filteredQuestions[i].answer ||
            this.filteredQuestions[i].answer == [])
        ) {
          unanswered += 1
        }
      }

      return unanswered
    },
    addCard(val) {
      this.$store
        .dispatch("travellersAndTrips/addPayment", val)
        .then(() => {
          if (!this.pathMixin_isMeeting) {
            return this.$store.dispatch(
              "bookTravel/addPersonalPaymentMethod",
              val
            )
          }
          return Promise.resolve()
        })
        .then(() => {
          // Once both actions are complete, set the payment method if useMultiplePaymentMethods is false
          if (!this.useMultiplePaymentMethods) {
            this.selectedPayments = [val]
          }
        })
        .catch((error) => {
          console.error("Error adding payment method: ", error)
        })
    },
    getCardLogo(name) {
      if (name) {
        return "/images/" + name.toLowerCase() + ".png"
      } else {
        return "/images/cc.png"
      }
    },
    /*
      loadProfile() {
        this.profile = JSON.parse(
          JSON.stringify(this.$globals.currentTravellerProfile)
        );
        this.getUdids();
      },
      */
    getUdids() {
      if (!this.isBusinessTrip) {
        this.loading = false
        return
      }
      var raw = {
        accountId: this.profile.account.id,
      }

      this.$app.huddleLabsServerCall("profile/udids", raw).then((result) => {
        this.questions = result.data
        this.loading = false
      })
    },
    fetchPaymentMethods() {
      this.$store.commit("bookTravel/CLEAR_PAYMENT_OPTIONS")
      const raw = {
        accountId: this.profile.account.id,
        contactId: this.profile.id,
        travelPolicyId: this.profile.travelPolicyId,
        tripType: this.tripType,
        solution: {},
      }
      if (this.solutionAll) {
        if (this.bookTrip.flight) {
          let airVendor =
            this.solutionAll.flight.airlineCode || this.solutionAll.flight
          raw.solution.flight = {
            vendorCode: this.solutionAll.flight.airlineCode || null,
            total: this.solutionAll.flight.pricing?.total || null,
          }

          if (this.solutionAll.flight.airlineCode == "Multiple") {
            raw.solution.flight.journeys = this.solutionAll.flight.journeys
          }
        }
        if (this.bookTrip.hotel) {
          raw.solution.hotel = {
            vendorCode: this.solutionAll.hotel.chainCode || null,
            total:
              this.solutionAll.hotel.requestedCurrencyPricing?.total || null,
          }
        }
        if (this.bookTrip.car) {
          raw.solution.car = {
            vendorCode: this.solutionAll.car.companyInfo?.code || null,
            total: this.solutionAll.car.pricingInfo?.total || null,
          }
        }
      }
      this.$app
        .huddleLabsServerCall("profile/segmented-payment-options", raw)
        .then((result) => {
          this.$store.dispatch("bookTravel/setPaymentOptions", result.data)
          this.loadingPaymentOptions = false

          // If a car direct billing relationship is found, find the company name
          const carDirectBilling = result.meta?.carDirectBilling
          const companyInfo = this.solutionAll?.car?.companyInfo

          if (carDirectBilling && companyInfo?.code === carDirectBilling) {
            this.carDirectBilingCompanyName = companyInfo.name
          }
        })
    },
    saveRegistrationTime() {
      var raw = {
        id: this.$store.state.meetings.meetingInfo.attendeeId,
        events: ["registrationCompleted"],
      }
      this.$app.huddleLabsServerCall("meetings/attendee/save", raw)
    },
    hoverChange() {
      this.hover = !this.hover
    },
    itinerary() {
      this.$router.push({ path: "/itinerary" })
    },
    async checkFlightPrice() {
      var segments = []
      var journey
      var leg
      for (journey of this.flightSolution.journeys) {
        for (leg of journey.legs) {
          var newSegment = {}
          newSegment.departureAirport = leg.departingAirport
          newSegment.arrivalAirport = leg.arrivingAirport
          newSegment.departureDate = leg.departingAt.date
          newSegment.arrivalDate = leg.arrivingAt.date
          newSegment.marketingCompany = leg.marketingAirline.code
          newSegment.operatingCompany = leg.operatingAirline.code
          newSegment.flightNumber = leg.flightNumber
          newSegment.bookingClass = "M"
          newSegment.segmentTattoo = 1
          newSegment.groupNumber = 1
          segments.push(newSegment)
        }
      }

      var raw = JSON.stringify({
        numberOfPassengers: 1,
        segments: segments,
      })

      this.$app
        .huddleLabsServerCall("amadeus/air/search", raw)
        .then((result) => {
          this.checkedPrice =
            100 *
            parseFloat(
              result.response.mainGroup.pricingGroupLevelGroup.fareInfoGroup
                .fareAmount.otherMonetaryDetails.amount
            )
        })
    },
    async getFareRules(flight) {
      return flight
    },
    onInput(event) {
      this.rawValue = event.target._vCleave.getRawValue()
      this.value = event.target._vCleave.getFormattedValue()
    },
    getPrice() {
      let price = 0
      // if (this.checkedPrice) {
      //   if(this.flightSolution.pricing.total == this.checkedPrice){
      //     price += this.flightSolution.pricing.total
      //   }
      //   else{
      //     price += this.checkedPrice
      //     this.priceChanged = true
      //   }
      // }
      if (this.flightSolution.pricing) {
        price += this.flightSolution.pricing.total
      }
      if (this.solutionAll.car.id) {
        price += this.solutionAll.car.pricingInfo.total
      }
      if (this.solutionAll.hotel.requestedCurrencyPricing) {
        price += this.solutionAll.hotel.requestedCurrencyPricing.total
      }
      return price
    },
    buildHotelSegment() {
      let hotelSegment = {
        checkInDate: this.solutionAll.hotel.startDate,
        checkOutDate: this.solutionAll.hotel.endDate,
        hotelCode: this.solutionAll.hotel.hotelCode,
        bookingCode: this.solutionAll.hotel.enhancedPricing.bookingCode,
        ratePlanCodes: this.solutionAll.hotel.requestedRateCodes, //array of codes used for live search request
        ratePlanCode: this.solutionAll.hotel.enhancedPricing.ratePlanCode, //rate code for selected room (for enhanced pricing request)
        roomTypeCode: this.solutionAll.hotel.enhancedPricing.roomTypeCode,
        guaranteeType: this.solutionAll.hotel.guaranteeType,
        infoSource: this.solutionAll.hotel.infoSource,
        roomCount: 1,
        guestCount: 1,
      }

      return hotelSegment
    },
    buildCarSegment() {
      let carSegment = {
        pickupDate: this.solutionAll.car.pickupInfo.dateTime,
        dropoffDate: this.solutionAll.car.dropoffInfo.dateTime,
        pickupLocation: this.solutionAll.car.pickupInfo.location,
        dropoffLocation: this.solutionAll.car.dropoffInfo.location,
        companyCode: this.solutionAll.car.companyInfo.code,
        vehicleInfo: {
          preferredVehicleType: this.solutionAll.car.vehicleInfo.code,
        },
        rateInformation: this.solutionAll.car.rateInformation,
        guaranteeInfo: this.solutionAll.car.guaranteeInfo,
      }

      return carSegment
    },
    buildCcEmails() {
      let ccEmails = []

      if (
        this.traveller.travelPolicy &&
        this.traveller.travelPolicy.ccEmail &&
        this.traveller.travelPolicy.ccEmail != this.traveller.email
      ) {
        ccEmails.push(this.traveller.travelPolicy.ccEmail)
      }

      // Travel arranger selected to be notified
      if (
        this.ccEmailActiveUser &&
        this.activeUser.email != this.traveller.email
      ) {
        if (!ccEmails.includes(this.ccEmailActiveUser)) {
          ccEmails.push(this.activeUser.email)
        }
      }

      // Traveler entered email address
      if (this.ccEmail && this.ccEmail != this.traveller.email) {
        if (!ccEmails.includes(this.ccEmail)) {
          ccEmails.push(this.ccEmail)
        }
      }

      //Ensure values are strings and contain an @ symbol
      return ccEmails.filter((email) => String(email).search(/@/) > 0)
    },
    buildUdids() {
      let udidAnswers = []

      this.filteredQuestions.map((udidQuestion) => {
        let udidAnswer = {}
        udidAnswer.number = udidQuestion.udidNumber.toString()
        udidAnswer.value = udidQuestion.answer ?? null
        udidAnswers.push(udidAnswer)
      })

      return udidAnswers
    },
    buildPolicy() {
      var business =
        this.$store.getters["travellersAndTrips/getTravellers"].tripType ==
        "business"
      let policy = {
        applied: business ? "Y" : "N",
        notes: this.policyNotes,
      }

      let segmentTravelPolicies = []
      let rulesTriggered = []
      let notifications = []
      let allRules = []

      if (this.traveller.travelPolicy) {
        allRules = this.traveller.travelPolicy.rules
      }

      if (business) {
        if (this.flightSolution.pricing) {
          segmentTravelPolicies.push(this.flightSolution.unfilteredTravelPolicy)
        }

        if (this.solutionAll.car.id) {
          segmentTravelPolicies.push(
            this.solutionAll.car.unfilteredTravelPolicy
          )
        }

        if (this.solutionAll.hotel.requestedCurrencyPricing) {
          segmentTravelPolicies.push(
            this.solutionAll.hotel.unfilteredTravelPolicy
          )
        }
      }

      // Loop through segments
      for (let segmentTravelPolicy of segmentTravelPolicies) {
        // Check if rules were triggered
        if (
          segmentTravelPolicy === undefined ||
          segmentTravelPolicy.rulesTriggered.length < 1
        ) {
          continue
        }

        // Loop through triggered rules
        for (let rule of segmentTravelPolicy.rulesTriggered) {
          // Add to list of rules triggered
          rulesTriggered.push(rule)

          // Find the matching rule with full details
          let matchingRule = allRules.find((allRule) => allRule.id == rule.id)

          if (!matchingRule) {
            continue
          }

          // Build list of actions/notifications
          for (let action of matchingRule.actions) {
            let notification = {
              type: action.type.toLowerCase(),
            }

            if (action.type == "SMS") {
              notification.destination = action.mobilePhone
            }
            if (action.type == "Email") {
              notification.destination = action.email
            }
            if (action.type == "Webhook") {
              notification.destination = action.url
            }

            // Check if the notification method/destination exists
            let notificationExists = notifications.some(
              (el) =>
                el.type == notification.type &&
                el.destination == notification.destination
            )

            // Add to the list if it doesn't exist
            if (!notificationExists) {
              notifications.push(notification)
            }
          }
        }
      }

      // 1 or more rules triggered
      policy.triggered = rulesTriggered.length > 0 ? "Y" : "N"
      policy.rulesTriggered = rulesTriggered

      // If at least one rule triggered
      if (rulesTriggered.length > 0) {
        // Find hard and soft approval triggered rules
        let hasHardApproval = rulesTriggered.find(
          (rule) => rule.approvalLevel == "hard"
        )
        let hasSoftApproval = rulesTriggered.find(
          (rule) => rule.approvalLevel == "soft"
        )

        if (hasHardApproval) {
          policy.mostStrictApprovalRequired = "hard"
        } else if (hasSoftApproval) {
          policy.mostStrictApprovalRequired = "soft"
        } else {
          policy.mostStrictApprovalRequired = "none"
        }
      }

      // Set the policy notification methods
      policy.notify = notifications

      return policy
    },
    buildUnaccompaniedMinor() {
      if (this.isUnaccompaniedMinor && this.unaccompaniedMinor) {
        return this.unaccompaniedMinor
      }
      return null
    },
    buildTravelerProfileForRequest(traveler) {
      let data = {}

      data.firstName = traveler.firstName
      data.lastName = traveler.lastName
      data.email = traveler.email
      data.mobilePhone = traveler.mobilePhone
      data.dateOfBirth = traveler.dateOfBirth
      data.gender = traveler.gender
      data.tsaPrecheckNumber = traveler.tsaPrecheckNumber || null

      return data
    },
    buildAccountForRequest(traveller) {
      let data = {}

      if (traveller.account) {
        data.gantAccountNumber = traveller.account.gantAccountNumber || null
        data.name = traveller.account.name || null
      }

      return data
    },
    buildBookedBy() {
      var bookedBy = {
        id: this.activeUser.id,
        email: this.activeUser.email,
      }

      return bookedBy
    },
    /*
      generateBookingKeys() {
        let keys = [];
        //booking key for the primary traveler (e.g. meeting attendee)
        let primaryKey = this.$helpers.randomString(8);
        keys.push({ key: primaryKey, guest: null });
  
        //generate booking keys for each guest on a separate pnr
        this.guestTravelers.forEach( guest => {
          let bookingKey = this.$helpers.randomString(8);
          keys.push({ key: bookingKey, guest: guest });
        });
        this.$store.commit("pnr/SET_BOOKING_QUEUE", keys);
        return keys;
      },
      */
    buildBookingQueue() {
      let queue = []

      //checkout for primary traveller
      let booking = this.buildCheckoutRequest()
      let parentKey = booking.bookingKey
      queue.push(booking)

      //checkout for each guest traveller
      this.guestTravelers.forEach((guest) => {
        queue.push(this.buildGuestCheckout(guest, parentKey))
      })

      this.$store.commit("pnr/SET_BOOKING_QUEUE", queue)
      //return queue;
    },
    buildNdcOrder(offer, traveler, journeys, programs) {
      //Build NDC Order Info
      let orderInfo = {}

      //Main offer representing the base fare before any ancillary items
      let fareOffer = {
        offerId: offer.offerId,
        ownerCode: offer.ownerCode,
        responseId: offer.responseId,
        items: offer.items.map((item) => {
          return {
            itemId: item.itemId,
            ptc: item.ptc,
            paxRef: item.paxRef,
          }
        }),
      }
      orderInfo.offers = [fareOffer]

      //Separate offer for any seat selections
      let seatOffer = this.buildSeatItems(journeys)
      if (seatOffer) {
        orderInfo.offers.push(seatOffer)
      }

      //Passenger details
      orderInfo.passengers = []
      let passenger = {
        id: 1,
        ptc: "ADT",
        birthdate: traveler.dateOfBirth,
        emailAddress: traveler.email,
        firstName: traveler.firstName,
        lastName: traveler.lastName,
        suffix: traveler.suffix || null,
        gender: traveler.gender,
        salesforceId: traveler.id,
        profileId: traveler.amadeusRecordLocator,
        travelerPrograms: programs,
        docInfo: [],
      }

      //Add TSA Precheck Number
      if (traveler.tsaPrecheckNumber) {
        passenger.docInfo.push({
          id: traveler.tsaPrecheckNumber,
          type: "K",
          issuerCountry: "US",
          expirationDate: null,
        })
      }

      //Add Passport
      if (traveler.passport) {
        passenger.docInfo.push({
          id: traveler.passport.number,
          type: "PT",
          issuerCountry: traveler.passport.nationality,
          expirationDate: traveler.passport.expirationDate,
          issueDate: traveler.passport.issuedDate,
        })
      }

      //Add Visa
      if (traveler.visa) {
        passenger.docInfo.push({
          id: traveler.visa.number,
          type: "V",
          issuerCountry: traveler.visa.nationality,
          expirationDate: traveler.visa.expiration,
        })
      }

      //Append middle name, if exists, to first name
      if (traveler.middleName) {
        passenger.firstName = traveler.firstName + " " + traveler.middleName
      }

      orderInfo.passengers.push(passenger)

      // Add payment time limit if available
      if (offer.paymentTimeLimitDateTime) {
        orderInfo.paymentTimeLimitDateTime = offer.paymentTimeLimitDateTime
      }

      return orderInfo
    },
    buildCheckoutRequest() {
      // Booking request object
      let payload = {
        meta: {
          stateful: true,
          sessionKey: null,
          step: null,
          preview: this.previewMode,
        },
        bookingKey: this.$helpers.randomString(10),
        trip: {
          recordLocator: this.tripLocator,
        },
        traveler: {
          id: this.traveller.id,
          profile: this.buildTravelerProfileForRequest(this.traveller),
        },
        unaccompaniedMinor: this.buildUnaccompaniedMinor(),
        bookedBy: this.buildBookedBy(),
        airSegment: null,
        carSegment: null,
        hotelSegment: null,
        meetingAttendeeId: null,
        tripName: this.tripName,
        tripDescription: this.tripDescription,
        paymentMethods: this.mappedPaymentMethods,
        ccEmails: this.buildCcEmails(),
        udids: this.buildUdids(),
        policy: this.buildPolicy(),
        travelerPrograms: this.travelerPrograms,
        account: this.buildAccountForRequest(this.traveller),
        requestedCurrency: this.traveller.defaultCurrency,
      }

      if (this.specialRequest.text) {
        payload.messageToAgent = this.specialRequest.text
      }

      if (this.selectedOffer) {
        let orderInfo = this.buildNdcOrder(
          this.selectedOffer,
          this.traveller,
          this.flightSolution.journeys,
          this.travelerPrograms
        )
        payload.ndcOrderInfo = orderInfo
      }

      //Can we remove this from the request for NDC bookings?
      if (this.flightSolution.pricing) {
        payload.airSegment = {}
        payload.airSegment.validatingCarrier = this.flightSolution.airlineCode
        payload.airSegment.journeys = this.flightSolution.journeys
      }

      // Add origin and destination only if selectedOffer exists
      if (this.selectedOffer) {
        Object.assign(payload.airSegment, {
          originCountryCode: this.flightSolution.originCountryCode,
          destinationCountryCode: this.flightSolution.destinationCountryCode,
        })
      }

      if (this.solutionAll.car.id) {
        payload.carSegment = this.buildCarSegment()
      }

      if (this.solutionAll.hotel.requestedCurrencyPricing) {
        payload.hotelSegment = this.buildHotelSegment()
      }

      if (this.solutionAll.hotel.infoSourceReference) {
        let nonGDSRateInfo = this.buildNonGDSRate()
        payload.nonGDSRateInfo = nonGDSRateInfo
      }

      if (this.pathMixin_isMeeting) {
        payload.meetingAttendeeId = this.$store.state.meetings.meetingInfo.attendeeId

        // Only set the payment method to the meeting payment method if one isn't already set
        if (this.selectedPayments.length === 0) {
          payload.paymentMethods = [{ id: this.paymentMethodMeeting }]
        }
      }

      if (this.isPersonalTrip) {
        payload.tripType = this.tripType
      }

      return payload
    },
    buildGuestCheckout(guest, parentKey) {
      let payload = {
        meta: {
          stateful: true,
          sessionKey: null,
          step: null,
          preview: this.previewMode,
        },
        bookingKey: this.$helpers.randomString(10),
        parentKey: parentKey,
        trip: {
          recordLocator: this.tripLocator,
        },
        traveler: {
          id: guest.profile.id,
          profile: this.buildTravelerProfileForRequest(guest.profile),
        },
        unaccompaniedMinor: this.buildUnaccompaniedMinor(),
        bookedBy: this.buildBookedBy(),
        airSegment: null,
        carSegment: null,
        hotelSegment: null,
        meetingAttendeeId: null,
        meetingAttendeeGuestId: guest.meetingAttendeeGuestId,
        tripName: this.tripName,
        tripDescription: this.tripDescription,
        paymentMethods: this.mappedPaymentMethods,
        ccEmails: this.buildCcEmails(),
        udids: this.buildUdids(),
        policy: this.buildPolicy(),
        travelerPrograms: guest.travelerPrograms,
        account: this.buildAccountForRequest(guest.profile),
        requestedCurrency: guest.profile.defaultCurrency,
      }

      if (this.specialRequest.text) {
        payload.messageToAgent = this.specialRequest.text
      }

      if (this.selectedOffer) {
        let orderInfo = this.buildNdcOrder(
          guest.result.selectedOffer,
          guest.profile,
          guest.result.flight.journeys,
          guest.travelerPrograms
        )
        payload.ndcOrderInfo = orderInfo
      }

      if (guest.result.flight.pricing) {
        payload.airSegment = {}
        payload.airSegment.validatingCarrier = guest.result.flight.airlineCode
        payload.airSegment.journeys = guest.result.flight.journeys
      }

      if (this.selectedOffer) {
        Object.assign(payload.airSegment, {
          originCountryCode: guest.result.flight.originCountryCode,
          destinationCountryCode: guest.result.flight.destinationCountryCode,
        })
      }

      if (this.pathMixin_isMeeting) {
        // Only set the payment method to the meeting payment method if one isn't already set TODO test this
        if (!this.selectedPayment) {
          payload.paymentMethods = [
            { id: this.paymentMethodMeeting, segments: [] },
          ]
        }
      }

      return payload
    },
    buildSeatItems(journeys) {
      let offer = null

      //retrieve selected seats
      let selectedSeats = []
      for (var journey of journeys) {
        for (var leg of journey.legs) {
          if (leg.selectedSeat) {
            selectedSeats.push({
              offerId: leg.selectedSeat.offerId,
              responseId: leg.selectedSeat.responseId,
              itemId: leg.selectedSeat.itemId,
              paxRef: 1, //leg.selectedSeat.paxRef, //TO DO: How to add OfferPrice paxRefID to SeatAvailability request?
              row: leg.selectedSeat.row,
              column: leg.selectedSeat.columnName,
            })
          }
        }
      }

      if (selectedSeats.length > 0) {
        //build seat items
        let seatItems = []
        for (var seat of selectedSeats) {
          seatItems.push({
            itemId: seat.itemId,
            paxRef: seat.paxRef,
            seat: {
              row: seat.row,
              column: seat.column,
            },
          })
        }

        //build selected offer
        offer = {
          offerId: selectedSeats[0].offerId,
          responseId: selectedSeats[0].responseId,
          //use the owner code from the fare offer
          //TO DO: Get the owner code from the SeatAvailability response
          ownerCode: this.selectedOffer.ownerCode,
          items: seatItems,
        }
      }

      return offer
    },
    submitCheckout() {
      console.log("submitCheckout")

      // Prevent multiple submissions
      if (this.checkout_pending) {
        console.log("Checkout already in progress, ignoring duplicate request")
        return
      }

      //build the pnr queue
      if (this.booking_queue.length <= 0) {
        this.buildBookingQueue()
      }

      //save the meeting attendee registration time
      if (this.pathMixin_isMeeting) {
        this.saveRegistrationTime()
      }

      console.log("this.booking_queue", this.booking_queue)

      // Set pending state immediately to prevent duplicate submissions
      this.checkout_pending = true

      this.$app.logEvent({
        event_type: "Checkout Submitted",
        event_properties: {
          cart_contents: this.cartContentForEventTracking,
          is_meeting_mode: this.pathMixin_isMeeting,
        },
      })

      this.$app
        .huddleLabsServerCall("amadeus/pnr/multi-checkout", {
          pnrs: this.booking_queue,
        })
        .then((response) => {
          //this.$store.commit("pnr/SET_PNR_PENDING", this.booking_queue[0]);
          window.hj("event", "checkout_submitted")
        })
        .catch((error) => {
          console.error("Checkout failed:", error)
          // Reset pending state on error to allow retry
          this.checkout_pending = false

          window.hj("event", "checkout_failed")

          this.$app.logEvent({
            event_type: "Checkout Failed",
            event_properties: {
              status: "failed",
              error: error.message || "Unknown error",
              cart_contents: this.cartContentForEventTracking,
              is_meeting_mode: this.pathMixin_isMeeting,
            },
          })
        })
    },
    pnrComplete(result) {
      console.log("completing pnr...")

      //if the first booking fails, clear the booking queue of any remaining (guest) pnrs
      if (!result.success) {
        this.$store.commit("pnr/SET_BOOKING_QUEUE", [])
        window.hj("event", "checkout_failed")

        this.$app.logEvent({
          event_type: "Checkout Failed",
          event_properties: {
            status: "failed",
            cart_contents: this.cartContentForEventTracking,
            is_meeting_mode: this.pathMixin_isMeeting,
          },
        })
        //Clear state.result and guest solution objects from Vuex?
      }

      //remove completed pnr from booking queue
      this.$store.commit("pnr/DEL_PNR_FROM_QUEUE", result.bookingKey)
      this.$store.commit("pnr/SAVE_BOOKING_STATUS", result)

      if (this.booking_queue.length == 0) {
        this.checkoutComplete()
      } else {
        console.log("going to next booking...")
        //2. get next pnr object from queue
        //let next = this.booking_queue[0];

        //3. set next pnr as active
        //this.$store.commit("pnr/SET_PNR_PENDING", next);
      }
    },
    checkoutComplete() {
      console.log("completing checkout...")
      // clear booking state
      this.$store.dispatch("bookTravel/clearBooking", {})
      //this.$store.commit("pnr/SET_PNR_PENDING", {});
      this.checkout_pending = false

      this.$app.logEvent({
        event_type: "Checkout Completed",
        event_properties: {
          status: "success",
          cart_contents: this.cartContentForEventTracking,
          is_meeting_mode: this.pathMixin_isMeeting,
        },
      })

      window.hj("event", "checkout_complete")

      //send to confirmation page
      if (this.isMeetingMode) {
        this.$router.push({ path: "/meetings/confirmation" })
      } else {
        this.$router.push({ path: "/pnr" })
      }
    },
    checkScreenSize() {
      if (window.innerWidth <= 769) {
        this.isDesktop = false
      }
    },
    getTravelPolicy() {
      if (!this.traveller.travelPolicyId) return

      var body = {
        travelPolicyId: this.traveller.travelPolicyId,
      }
      this.$app
        .huddleLabsServerCall("profile/travel-policy", body)
        .then((result) => {
          this.travelPolicy = result.data
        })
    },
    selectPayments(payments) {
      this.selectedPayments = payments
    },
    mapPaymentMethods(payments) {
      return payments.map((payment) => {
        return {
          id: payment.id,
          segments: payment.segments || [],
          //amount: payment.amount || null
        }
      })
    },
    toggleMultiplePayments(val) {
      this.useMultiplePaymentMethods = val
    },
    buildNonGDSRate() {
      let nonGDSRateInfo = {
        hotelCode: this.solutionAll.hotel.infoSourceReference,
        ratePlanCode: this.solutionAll.hotel.enhancedPricing.ratePlanCode,
        roomTypeCode: this.solutionAll.hotel.enhancedPricing.roomTypeCode,
        originalPricing: this.solutionAll.hotel.originalPricing,
        textDescription: this.solutionAll.hotel.textDescription,
        infoSource: this.solutionAll.hotel.infoSource,
        guaranteeType: this.solutionAll.hotel.guaranteeType,
      }

      return nonGDSRateInfo
    },
    isProgramDisabled(program) {
      return (
        program.type === "hotel" && this.hotelBookingDetails?.infoSource == "BK"
      )
    },
    vendorMessage(program) {
      if (
        program.type === "hotel" &&
        this.hotelBookingDetails?.infoSource == "BK"
      ) {
        return "*Frequent travel numbers are not valid for Booking.com rates."
      }
      return null // Return null if no message should be displayed
    },
    isWebBedsHotel(infoSource) {
      return infoSource == "SJ"
    },
    isMemberRateProgram(program) {
      return (
        program.type === 'hotel' &&
        this.solutionAll.hotel &&
        this.solutionAll.hotel.isMemberRate === true
      )
    },
  },

  mounted() {
    // Set the cart contents so it can be used for all checkout event logging
    this.cartContentForEventTracking = {
      has_air: !!this.flightSolution.pricing,
      has_car: !!this.solutionAll.car.id,
      has_hotel: !!this.solutionAll.hotel.requestedCurrencyPricing,
      total_price: this.getPrice(),
      car_base_price: this.pricingInfo.car?.fare || 0,
      car_tax_price: 0,
      car_total_price: this.pricingInfo.car?.total || 0,
      flight_base_price: this.flightSolution.pricing?.fare || 0,
      flight_tax_price: this.flightSolution.pricing?.tax || 0,
      flight_total_price: this.flightSolution.pricing?.total || 0,
      hotel_base_price: this.pricingInfo.hotel?.fare || 0,
      hotel_tax_price: this.pricingInfo.hotel?.tax || 0,
      hotel_total_price: this.pricingInfo.hotel?.total || 0,
      hotel_rate_plan_code: this.solutionAll?.hotel?.enhancedPricing?.ratePlanCode || null,
    }

    this.$app.logEvent({
      event_type: "Checkout Started",
      event_properties: {
        cart_contents: this.cartContentForEventTracking,
        is_meeting_mode: this.pathMixin_isMeeting,
      },
    })

    this.checkScreenSize()
    this.fetchPaymentMethods()
    this.getUdids()
    //this.loadProfile();
    this.getTravelPolicy()

    //Pull relevant travel programs from profile
    this.travelerPrograms = this.findRelevantPrograms(
      this.traveller.travelerPrograms
    )

    this.guestTravelers.forEach((guest) => {
      this.$set(
        guest,
        "travelerPrograms",
        this.findRelevantPrograms(guest.travelerPrograms)
      )
    })

    // If a booking was in-flight on refresh, re-open the modal to resume polling.
    if (this.booking_queue.length > 0) {
      this.checkout_pending = true
    }

    //clear any previously saved pending checkout state
    this.$store.dispatch("bookTravel/setBooked", false)
    //this.$store.commit("pnr/SET_PNR_PENDING", {});
    this.$store.commit("pnr/CLEAR_BOOKING_STATUS")

    //Initalize NDC timer
    if (this.selectedOffer?.expireTime) {
      this.$nextTick(() => {
        let expireTime = moment.utc(this.selectedOffer.expireTime)
        this.countdown =
          expireTime > moment.utc()
            ? Math.abs(moment().diff(expireTime, "seconds"))
            : 0
        this.countdownTimer()
      })
    }

    if (this.bookingForSomeoneElse) {
      this.ccEmailActiveUser = true // Automatically check the box
    }
  },
}
</script>

<style scoped>
.scrollable-container {
  overflow-y: auto;
  overflow-x: hidden;
}

/*.bookingButtonContainer {
  position: relative;
  bottom: -25px;
}

.bookingButtons {
  position: relative;
}

.buttonContainer {
  position: absolute;
  z-index: 20;
} */

.payment-card-header {
  height: 35px;
}

/* Card name adjusted for 2 lines */
.payment-card-name {
  height: calc(var(--line-height) * 2em);
  line-height: var(--line-height);
  --line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.clickable-card {
  cursor: pointer;
}

/* Wraps label and aligns with price value */
.itemized-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
}

.itemized-col {
  flex: 1;
  min-width: 0;
}

.itemized-col + .itemized-col {
  text-align: right;
  white-space: nowrap;
}

@media screen and (max-width: 601px) {
  /* .bookingButtonContainer {
    bottom: -10px;
  }

  .buttonContainer {
    left: 41%;
    width: 61%;
    top: 30px;
  } 

  .bookingButtons {
    left: -41%;
  }*/

  .payment-card {
    width: calc(100% - 0.5rem);
  }
}

@media screen and (min-width: 602px) and (max-width: 768px) {
  /* .buttonContainer {
    left: 41%;
    width: 61%;
    top: 50px;
  } 

  .bookingButtons {
    left: -41%;
  }*/

  .payment-card {
    width: calc(50% - 0.5rem);
  }
}

@media screen and (min-width: 769px) and (max-width: 1215px) {
  /* .buttonContainer {
    left: 42%;
    width: 61%;
    top: 30px;
  }

  .bookingButtons {
    left: -42%;
  } */

  .payment-card {
    width: calc(50% - 0.5rem);
  }
}

@media screen and (min-width: 1216px) {
  /* .buttonContainer {
    left: 41%;
    width: 61%;
    top: 70px;
  }

  .bookingButtons {
    left: -41%;
  } */

  .payment-card {
    width: calc(33% - 0.5rem);
  }
}
</style>
